import { useState, useEffect, useCallback } from 'react';
import { useStore } from 'hooks/useStore';
import { useInterval } from 'hooks/usePolling';
import {
  RoomFilterData,
  RoomFilterPayload
} from 'Interfaces/HsRoomPriorityInterfaces';
import { getJitteredInterval } from 'utils/pollingUtility';
import {toast} from 'react-toastify';

// Fix the export to be a default export
const useHsRoomPriority = () => {
  const { hsRoomPriorityStore, roomStore, shiftTimingStore } = useStore();
  const [expandedRooms, setExpandedRooms] = useState<Record<string, boolean>>({});
  const [hasReceivedResponse, setHasReceivedResponse] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Global filters for all rooms
  const [globalDateFilter, setGlobalDateFilter] = useState<Date>(new Date());
  const [globalShiftFilter, setGlobalShiftFilter] = useState<string>('First');
  const [isFiltering, setIsFiltering] = useState<boolean>(false);

  const POLLING_DELAY = getJitteredInterval();
  const environment = import.meta.env.VITE_ENVIRONMENT;

  // Format date to YYYY-MM-DD for API - corrected to handle timezone issues
  const formatDateForApi = (date: Date): string => {
    // Use local timezone values to prevent date shift issues
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Format date as MM/DD/YYYY for display (using slash format as requested)
  const formatDateForDisplay = (dateInput: Date | string): string => {
    if (!dateInput) {
      return '';
    }

    // Handle Date object input
    if (dateInput instanceof Date) {
      const year = dateInput.getFullYear();
      const month = String(dateInput.getMonth() + 1).padStart(2, '0');
      const day = String(dateInput.getDate()).padStart(2, '0');
      return `${month}/${day}/${year}`;
    }

    // Handle string input
    const dateStr = String(dateInput);

    // Handle API format (YYYY-MM-DD)
    if (dateStr && typeof dateStr === 'string' && dateStr.includes && dateStr.includes('-')) {
      const [year, month, day] = dateStr.split('-');
      // Ensure month and day have leading zeros
      const formattedMonth = month.padStart(2, '0');
      const formattedDay = day.padStart(2, '0');
      return `${formattedMonth}/${formattedDay}/${year}`;
    }

    return String(dateInput);
  };

  // Function to determine the current shift based on time of day
  const setDefaultShiftBasedOnCurrentTime = () => {
    if (shiftTimingStore.shiftTimings.length === 0) {
      return;
    }

    const now = new Date();
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();

    // Convert current time to minutes since midnight for easier comparison
    const currentTimeInMinutes = currentHour * 60 + currentMinute;

    // Find the appropriate shift based on start and end times
    let appropriateShift = 'First'; // Default to First shift if none found

    // Convert shift timings to minutes and find which shift we're currently in
    for (const shift of shiftTimingStore.shiftTimings) {
      // Parse shift times (assuming format is HH:MM or HH:MM:SS)
      const startParts = shift.start_time.split(':');
      const startTimeInMinutes = parseInt(startParts[0]) * 60 + parseInt(startParts[1]);

      const endParts = shift.end_time.split(':');
      const endTimeInMinutes = parseInt(endParts[0]) * 60 + parseInt(endParts[1]);

      // Handle shifts that cross midnight (end time < start time)
      if (endTimeInMinutes < startTimeInMinutes) {
        // Current time is after start time OR before end time
        if (
          currentTimeInMinutes >= startTimeInMinutes ||
          currentTimeInMinutes <= endTimeInMinutes
        ) {
          appropriateShift = shift.shift_name;
          break;
        }
      } else {
        // Normal shift (doesn't cross midnight)
        if (
          currentTimeInMinutes >= startTimeInMinutes &&
          currentTimeInMinutes <= endTimeInMinutes
        ) {
          appropriateShift = shift.shift_name;
          break;
        }
      }
    }

    // Set global shift filter with the determined shift
    setGlobalShiftFilter(appropriateShift);
  };

  // Fetch shift schedules and set current shift
  useEffect(() => {
    // Load shifts if not already loaded
    if (shiftTimingStore.shiftTimings.length === 0) {
      shiftTimingStore.fetchShiftTimings(1, 100);
    } else {
      setDefaultShiftBasedOnCurrentTime();
    }
  }, [shiftTimingStore.shiftTimings]);

  // When shifts load, set the current shift
  useEffect(() => {
    if (shiftTimingStore.shiftTimings.length > 0 && !shiftTimingStore.isLoading) {
      setDefaultShiftBasedOnCurrentTime();
    }
  }, [shiftTimingStore.isLoading]);

  // Polling callback function
  const fetchDataForPolling = useCallback(
    async (isPollingRefresh = false) => {
      try {
        // Only set filtering state for initial loads, not for polling refreshes
        if (!isPollingRefresh) {
          setIsFiltering(true);
        }

        const formattedDate = formatDateForApi(globalDateFilter);
        const allRoomCodes = roomStore.rooms.map(room => room.room_short);

        if (allRoomCodes.length === 0) {
          if (!isPollingRefresh) {
            setIsFiltering(false);
          }
          return;
        }

        // Create a payload where each room has the same date and shift
        const payload: RoomFilterPayload = {};
        allRoomCodes.forEach(roomCode => {
          payload[roomCode] = {
            date: formattedDate,
            shift: globalShiftFilter
          };
        });

        // Pass false for setLoadingState during polling to avoid showing loading indicator
        await hsRoomPriorityStore.fetchRoomPriorities(payload, !isPollingRefresh);
      } catch (error) {
        if(environment === 'development'){
          console.error('Error in polling fetch:', error);
        }
        toast.error('Error getting HS Room Priorities');
      } finally {
        if (!isPollingRefresh) {
          setIsFiltering(false);
        }

        // Mark initial load as complete after first data fetch
        if (isInitialLoad) {
          setIsInitialLoad(false);
        }
      }
    },
    [hsRoomPriorityStore, roomStore.rooms, globalDateFilter, globalShiftFilter, isInitialLoad]
  );

  // Initial data fetch effect - only runs once when rooms and shifts are loaded
  useEffect(() => {
    // Load rooms if not already loaded
    if (roomStore.rooms.length === 0) {
      roomStore.fetchRooms(1, 100);
    }
  }, []);

  // Separate effect for initial data load - only runs once when all prerequisites are met
  useEffect(() => {
    let isMounted = true;

    const loadInitialData = async () => {
      if (roomStore.rooms.length > 0 && shiftTimingStore.shiftTimings.length > 0 && isInitialLoad) {
        if (isMounted) {
          try {
            setIsFiltering(true);

            const formattedDate = formatDateForApi(globalDateFilter);
            const allRoomCodes = roomStore.rooms.map(room => room.room_short);

            if (allRoomCodes.length === 0) {
              setIsFiltering(false);
              return;
            }

            const payload: RoomFilterPayload = {};
            allRoomCodes.forEach(roomCode => {
              payload[roomCode] = {
                date: formattedDate,
                shift: globalShiftFilter
              };
            });

            await hsRoomPriorityStore.fetchRoomPriorities(payload, true);
            setIsInitialLoad(false);
          } catch (error) {
            if(environment === 'development'){
              console.error('Error in initial data load:', error);
            }
            toast.error('Error getting HS Room Priorities');
          } finally {
            setIsFiltering(false);
          }
        }
      }
    };

    loadInitialData();

    return () => {
      isMounted = false;
    };
  }, [
    roomStore.rooms.length,
    shiftTimingStore.shiftTimings.length,
    isInitialLoad,
    globalDateFilter,
    globalShiftFilter,
    hsRoomPriorityStore
  ]);

  // Reset the response flag when loading changes to true
  useEffect(() => {
    if (hsRoomPriorityStore.loading === true) {
      setHasReceivedResponse(false);
    }
  }, [hsRoomPriorityStore.loading]);

  // Set the response flag when we receive data (when loading becomes false)
  useEffect(() => {
    if (hsRoomPriorityStore.loading === false) {
      setHasReceivedResponse(true);
      setIsFiltering(false);

      // Expand all rooms by default
      const initialExpandedState: Record<string, boolean> = {};
      hsRoomPriorityStore.roomCodes.forEach(roomCode => {
        initialExpandedState[roomCode] = true;
      });
      setExpandedRooms(initialExpandedState);
    }
  }, [hsRoomPriorityStore.loading]);

  const toggleRoom = (roomCode: string) => {
    setExpandedRooms(prev => ({
      ...prev,
      [roomCode]: !prev[roomCode]
    }));
  };

  // Format number values for display
  const formatNumber = (value: number | null): string => {
    if (value === null || value === undefined) {
      return '-';
    }
    return value.toString();
  };

  // Format the complete status - empty for printing
  const completeBodyTemplate = (): string => {
    return ''; // Return empty string for printing
  };

  // Check if we have any room data
  const hasValidData = () => {
    return hsRoomPriorityStore.roomCodes.length > 0 && hsRoomPriorityStore.hasData;
  };

  // Get room name from code for display
  const getRoomNameFromCode = (code: string): string => {
    const room = roomStore.rooms.find(r => r.room_short === code);
    return room ? room.room_long : code;
  };

  // Handle global date filter change
  const handleGlobalDateChange = (date: Date | null) => {
    if (date) {
      setGlobalDateFilter(date);
    }
  };

  // Handle global shift filter change
  const handleGlobalShiftChange = (shift: string) => {
    if (shift) {
      setGlobalShiftFilter(shift);
    }
  };

  // Apply global filters to all rooms and fetch data
  const applyGlobalFilters = async () => {
    setIsFiltering(true);

    try {
      // Create a payload with the same date and shift for all rooms
      const formattedDate = formatDateForApi(globalDateFilter);

      // Get all room codes first
      const allRoomCodes = roomStore.rooms.map(room => room.room_short);

      if (allRoomCodes.length === 0) {
        setIsFiltering(false);
        return;
      }

      // Create a payload where each room has the same date and shift
      const payload: RoomFilterPayload = {};
      allRoomCodes.forEach(roomCode => {
        payload[roomCode] = {
          date: formattedDate,
          shift: globalShiftFilter
        };
      });

      await hsRoomPriorityStore.fetchRoomPriorities(payload, true); // Set loading state for filter updates
    } catch (error) {
      if(environment === 'development'){
        console.error('Error applying global filters:', error);
      }
      toast.error('Error applying global filters');
    } finally {
      setIsFiltering(false);
    }
  };

  // Polling functionality - refresh data every 10 seconds
  useInterval(() => {
    if (!hsRoomPriorityStore.loading && !isFiltering) {
      fetchDataForPolling(true); // Pass true to indicate this is a polling refresh
    }
  }, POLLING_DELAY);

  // Get current filter for a room
  const getRoomFilter = (roomCode: string): RoomFilterData | undefined => {
    return hsRoomPriorityStore.getRoomFilter(roomCode);
  };

  // Parse date string to Date object
  const parseDate = (dateStr: string): Date => {
    if (!dateStr) {
      return new Date();
    }
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  // Prepare for printing
  const prepareForPrinting = () => {
    return new Promise<void>(resolve => {
      setIsPrinting(true);
      setTimeout(() => {
        resolve();
      }, 500);
    });
  };

  // Complete printing operation
  const completePrinting = () => {
    setIsPrinting(false);
  };

  return {
    loading: (isInitialLoad && hsRoomPriorityStore.loading) || isFiltering,
    loadingShifts: shiftTimingStore.isLoading,
    expandedRooms,
    hasReceivedResponse,
    toggleRoom,
    formatNumber,
    completeBodyTemplate,
    hasValidData,
    getRoomNameFromCode,
    formatDateForDisplay,
    shifts: shiftTimingStore.shiftTimings,
    setExpandedRooms,
    isPrinting,
    prepareForPrinting,
    completePrinting,
    getRoomFilter,
    parseDate,
    globalDateFilter,
    globalShiftFilter,
    handleGlobalDateChange,
    handleGlobalShiftChange,
    applyGlobalFilters,
    hsRoomPriorityStore
  };
};

export default useHsRoomPriority;
